import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { hasMention, mentionLabels } from '../utils/commentMentions';

export interface TaskComment {
  id: string; project_id: string; task_id: string; author_id: string | null;
  body: string; mentioned_user_ids: string[]; created_at: string; edited_at: string | null; deleted_at: string | null;
}
/** `name` is the member's mention label: unique within the project (see mentionLabels). */
export interface CommentMember { id: string; name: string }
interface State {
  threads: Record<string, TaskComment[]>;
  names: Record<string, string>;
  counts: Record<string, Record<string, number>>;
  errors: Record<string, string | undefined>;
  countErrors: Record<string, string | undefined>;
  loading: Record<string, boolean>;
}
export const commentKey = (projectId: string, taskId: string) => JSON.stringify([projectId, taskId]);
export const useProjectComments = create<State>(() => ({ threads: {}, names: {}, counts: {}, errors: {}, countErrors: {}, loading: {} }));
const message = (e: unknown) => e && typeof e === 'object' && 'message' in e ? String(e.message) : 'Comments could not load. Please try again.';
const fields = 'id, project_id, task_id, author_id, body, mentioned_user_ids, created_at, edited_at, deleted_at';
async function userId() {
  const { user } = await syncManager.safeAuthCheck();
  if (!user) throw new Error('Sign in to use comments.');
  return user.id;
}
const generations = new Map<string, number>();
export async function fetchTaskComments(projectId: string, taskId: string): Promise<void> {
  const key = commentKey(projectId, taskId);
  const generation = (generations.get(key) ?? 0) + 1; generations.set(key, generation);
  useProjectComments.setState(s => ({ loading: { ...s.loading, [key]: true } }));
  try {
    await userId();
    const rows: TaskComment[] = [];
    for (let offset = 0; ; offset += 500) {
      const { data, error } = await supabase.from('project_task_comments').select(fields)
        .eq('project_id', projectId).eq('task_id', taskId)
        .order('created_at', { ascending: true }).order('id', { ascending: true }).range(offset, offset + 499);
      if (error) throw error;
      rows.push(...(data ?? []) as TaskComment[]);
      if ((data?.length ?? 0) < 500) break;
    }
    const ids = [...new Set(rows.flatMap(row => row.author_id ? [row.author_id] : []))];
    let names: Record<string, string> = {};
    if (ids.length) {
      const { data, error } = await supabase.from('user_profiles').select('id, name, handle').in('id', ids);
      if (error) throw error;
      names = Object.fromEntries((data ?? []).map(p => [p.id, p.name || p.handle || 'Project member']));
    }
    if (generations.get(key) !== generation) return;
    useProjectComments.setState(s => ({ threads: { ...s.threads, [key]: rows }, names: { ...s.names, ...names }, errors: { ...s.errors, [key]: undefined } }));
  } catch (error) {
    if (generations.get(key) === generation) useProjectComments.setState(s => ({ threads: { ...s.threads, [key]: [] }, errors: { ...s.errors, [key]: message(error) } }));
  } finally {
    if (generations.get(key) === generation) useProjectComments.setState(s => ({ loading: { ...s.loading, [key]: false } }));
  }
}
export async function fetchCommentCounts(projectId: string): Promise<void> {
  try {
    await userId();
    // One query for the open project, including soft-deleted placeholders.
    const { data, count, error } = await supabase.from('project_task_comments').select('task_id', { count: 'exact' }).eq('project_id', projectId);
    if (error) throw error;
    if (count !== null && count > (data?.length ?? 0)) throw new Error('Comment counts are unavailable for this large project. Open a thread to read all comments.');
    const counts: Record<string, number> = {};
    for (const row of data ?? []) counts[row.task_id] = (counts[row.task_id] ?? 0) + 1;
    useProjectComments.setState(s => ({ counts: { ...s.counts, [projectId]: counts }, countErrors: { ...s.countErrors, [projectId]: undefined } }));
  } catch (error) {
    useProjectComments.setState(s => ({ counts: { ...s.counts, [projectId]: {} }, countErrors: { ...s.countErrors, [projectId]: message(error) } }));
  }
}
/** Fresh roster, including the owner even when they have no membership row. */
export async function fetchCommentMembers(projectId: string): Promise<CommentMember[]> {
  const me = await userId();
  const { data: project, error: projectError } = await supabase.from('projects').select('owner_id').eq('id', projectId).maybeSingle();
  if (projectError) throw projectError;
  if (!project) throw new Error('This shared project is no longer available.');
  const { data: members, error } = await supabase.from('project_members').select('user_id').eq('project_id', projectId);
  if (error) throw error;
  const ids = [...new Set([project.owner_id, ...(members ?? []).map(m => m.user_id)])].filter(id => id !== me);
  if (!ids.length) return [];
  const { data: profiles, error: profilesError } = await supabase.from('user_profiles').select('id, name, handle').in('id', ids);
  if (profilesError) throw profilesError;
  const candidates = ids.map(id => {
    const profile = profiles?.find(p => p.id === id);
    return { id, name: profile?.name || profile?.handle || 'Project member', handle: profile?.handle ?? null };
  });
  const labels = mentionLabels(candidates);
  return candidates.map(({ id }) => ({ id, name: labels.get(id)! }));
}
export async function saveTaskComment(projectId: string, taskId: string, body: string, mentions: CommentMember[], id?: string): Promise<{ dropped: boolean }> {
  const me = await userId();
  const text = body.trim();
  if (!text || text.length > 4000) throw new Error('Write a comment of 1–4000 characters.');
  if (mentions.length > 20) throw new Error('A comment can mention at most 20 members.');
  const current = mentions.length ? await fetchCommentMembers(projectId) : [];
  const requested = mentions.filter(m => hasMention(text, m.name));
  const allowed = [...new Set(requested.filter(m => m.id !== me && current.some(c => c.id === m.id)).map(m => m.id))];
  const query = supabase.from('project_task_comments');
  const write = id
    ? query.update({ body: text, mentioned_user_ids: allowed }).eq('id', id).eq('author_id', me).eq('project_id', projectId).eq('task_id', taskId).is('deleted_at', null)
    : query.insert({ project_id: projectId, task_id: taskId, author_id: me, body: text, mentioned_user_ids: allowed });
  const { data, error } = await write.select('id').single();
  if (error) throw error;
  if (!data) throw new Error('Comment was not saved. Check your access and try again.');
  await Promise.all([fetchTaskComments(projectId, taskId), fetchCommentCounts(projectId)]);
  return { dropped: requested.some(m => !allowed.includes(m.id)) };
}
export async function deleteTaskComment(row: TaskComment): Promise<void> {
  const me = await userId();
  const { data, error } = await supabase.from('project_task_comments').update({ deleted_at: new Date().toISOString() })
    .eq('id', row.id).eq('author_id', me).eq('project_id', row.project_id).is('deleted_at', null).select('id').single();
  if (error) throw error;
  if (!data) throw new Error('Comment was not deleted. Check your access and try again.');
  await Promise.all([fetchTaskComments(row.project_id, row.task_id), fetchCommentCounts(row.project_id)]);
}
export function subscribeComments(projectId: string, taskId?: string): () => void {
  try {
    const channel = supabase.channel(`project-comments-${projectId}-${taskId ?? 'counts'}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'project_task_comments', filter: `project_id=eq.${projectId}` }, () => {
        void fetchCommentCounts(projectId);
        if (taskId) void fetchTaskComments(projectId, taskId);
      }).subscribe();
    return () => { void supabase.removeChannel(channel); };
  } catch { return () => {}; }
}
export async function resolveCommentTarget(id: string): Promise<{ projectId: string; taskId: string } | null> {
  try {
    await userId();
    const { data, error } = await supabase.from('project_task_comments').select('project_id, task_id').eq('id', id).maybeSingle();
    if (error || !data) return null;
    const { data: project, error: projectError } = await supabase.from('projects').select('data').eq('id', data.project_id).maybeSingle();
    if (projectError || !project || !project.data?.tasks?.some((task: { id: string }) => task.id === data.task_id)) return null;
    return { projectId: data.project_id, taskId: data.task_id };
  } catch { return null; }
}
